import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { knessetFromParam, previousKnesset, swornInLabel, useScope } from '../hooks/useScope';
import { formatDayMonth, useSyncPause } from '../hooks/useLastSync';
import KnessetMenu from '../components/KnessetMenu';
import Notice from '../components/Notice';
import { useBreakpoint } from '../useBreakpoint';
import Grid from '../layout/Grid';
import FilterSection from '../layout/FilterSection';
import PageContainer from '../layout/PageContainer';
import TwoPane from '../layout/TwoPane';
import { BillListEntry, BillStatsDoc, getAllBills, getBillStats, getRecentBills, getScope } from '../data/cache';
import { stageInfo } from '../utils/stages';
import { buttonReset, color, font, radius, space, TAP, weight } from '../theme';
import PageHeader from '../components/PageHeader';
import SearchField from '../components/SearchField';
import Chip, { ChipRow } from '../components/Chip';
import SelectMenu, { ResultBar, SelectOption } from '../components/SelectMenu';
import { Card, CardHeader } from '../components/Card';
import { Badge, StageBadge } from '../components/Badge';
import ListRow from '../components/ListRow';
import Button from '../components/Button';
import { EmptyState, ErrorState } from '../components/States';
import { SkeletonList } from '../components/Skeleton';

/* ── Config ─────────────────────────────────────────────────────────────── */

const typeLabels: Record<string, string> = {
  private: 'פרטית',
  government: 'ממשלתית',
  committee: 'ועדה',
};

const ALL_TOPICS = ['ביטחון', 'כלכלה', 'דת ומדינה', 'חברה', 'משפט', 'חינוך', 'בריאות', 'סביבה', 'חוץ', 'תחבורה', 'כללי'];

type StageFilter = 'הכל' | string;
type TypeFilter = 'הכל' | 'private' | 'government' | 'committee';
type SortMode = 'newest' | 'oldest' | 'progressed' | 'topic';

// Both date sorts use lastUpdated (the bill's last Knesset change), so the labels say "updated"
const SORT_OPTIONS: { key: SortMode; label: string }[] = [
  { key: 'newest', label: 'עודכן לאחרונה' },
  { key: 'oldest', label: 'עודכן הכי מזמן' },
  { key: 'progressed', label: 'לפי התקדמות' },
];

// Stages in legislative order (funnel, desktop filter grid); the short label where the full one would wrap
const STAGE_ORDER = ['דיון מוקדם', 'בוועדה', 'קריאה ראשונה', 'קריאה שנייה ושלישית', 'עברה', 'נעצרה'];
const SHORT_STAGE: Record<string, string> = { 'קריאה שנייה ושלישית': 'קריאה ב׳+ג׳' };
const FUNNEL = ['דיון מוקדם', 'בוועדה', 'קריאה ראשונה', 'קריאה שנייה ושלישית', 'עברה'];
// Phone stage chips: outcomes first, then the stages in progress
const PHONE_STAGES = ['עברה', 'קריאה ראשונה', 'קריאה שנייה ושלישית', 'בוועדה', 'דיון מוקדם', 'נעצרה'];
const ENDED = ['נעצרה', 'מוזגה', 'הוסבה'];

const PAGE_SIZE = 30;

/* ── Component ──────────────────────────────────────────────────────────── */

const LegislationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDesktop } = useBreakpoint();

  // Filters live in the URL
  const stageFilter: StageFilter = searchParams.get('stage') || 'הכל';
  const typeFilter: TypeFilter = (searchParams.get('type') as TypeFilter) || 'הכל';
  const topicFilter: string = searchParams.get('topic') || 'הכל';
  const search: string = searchParams.get('q') || '';
  const sortMode: SortMode = (searchParams.get('sort') as SortMode) || 'newest';
  const visibleCount: number = Math.max(PAGE_SIZE, parseInt(searchParams.get('count') || String(PAGE_SIZE), 10) || PAGE_SIZE);

  const [stats, setStats] = useState<BillStatsDoc | null>(null);
  const [bills, setBills] = useState<BillListEntry[] | null>(null);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const scope = useScope();
  // The Knesset shown: the current one, or an archived one (?k=). Data loads resolve it from
  // the loaded scope (getScope), so a first render's default scope never loads the wrong list.
  const kParam = searchParams.get('k');
  const viewKnesset = knessetFromParam(kParam, scope);
  const viewingCurrent = viewKnesset === scope.knesset;
  const pausedSince = useSyncPause('legislation');
  // Another Knesset is a fresh list: filters and paging reset
  const setKnesset = (k: number) => setSearchParams(() => {
    const next = new URLSearchParams();
    if (k !== scope.knesset) next.set('k', String(k));
    return next;
  }, { replace: true });

  const updateParam = useCallback((key: string, value: string, defaultValue: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value === defaultValue) next.delete(key);
      else next.set(key, value);
      // Reset count when any filter changes (except count itself)
      if (key !== 'count') next.delete('count');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setStageFilter = (v: StageFilter) => updateParam('stage', v, 'הכל');
  const setTypeFilter = (v: TypeFilter) => updateParam('type', v, 'הכל');
  const setTopicFilter = (v: string) => updateParam('topic', v, 'הכל');
  const setSearch = (v: string) => updateParam('q', v, '');
  const setSortMode = (v: SortMode) => updateParam('sort', v, 'newest');
  const setVisibleCount = (v: number) => updateParam('count', String(v), String(PAGE_SIZE));

  // The default view (newest first, no filters) needs only the newest bills
  const isDefaultView = stageFilter === 'הכל' && typeFilter === 'הכל' && topicFilter === 'הכל' && !search.trim() && sortMode === 'newest';
  const needAll = !isDefaultView || (bills !== null && visibleCount > bills.length);

  // Counts first (1 KB), then the newest bills; the full list only when the view needs it
  useEffect(() => {
    let cancelled = false;
    setError(false);
    setStats(null);
    setBills(null);
    setComplete(false);
    getScope().then(sc => {
      const k = knessetFromParam(kParam, sc);
      getBillStats(k).then(s => { if (!cancelled) setStats(s); }).catch(() => { /* ok — counts fall back to the list */ });
      return getRecentBills(k).then(r => {
        if (cancelled) return;
        setBills(prev => (prev && prev.length > r.bills.length ? prev : r.bills));
        if (r.complete) setComplete(true);
      });
    }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [reloadKey, kParam]);

  useEffect(() => {
    if (!needAll || complete) return;
    let cancelled = false;
    getScope().then(sc => getAllBills(knessetFromParam(kParam, sc))).then(all => {
      if (cancelled) return;
      setBills(all);
      setComplete(true);
    }).catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [needAll, complete, reloadKey, kParam]);

  // Scroll restoration: save on unmount, restore once the list has loaded
  const restored = useRef(false);
  useEffect(() => () => {
    try { sessionStorage.setItem('legislation_scroll', String(window.scrollY)); } catch { /* ok */ }
  }, []);
  const listReady = bills !== null && (!needAll || complete);
  useEffect(() => {
    if (restored.current || !listReady) return;
    restored.current = true;
    try {
      const saved = sessionStorage.getItem('legislation_scroll');
      if (saved) { window.scrollTo(0, parseInt(saved, 10) || 0); sessionStorage.removeItem('legislation_scroll'); }
    } catch { /* ok */ }
  }, [listReady]);

  const filtered = useMemo(() => {
    let result = bills || [];
    if (stageFilter !== 'הכל') result = result.filter(b => b.simpleStage === stageFilter);
    if (typeFilter !== 'הכל') result = result.filter(b => b.billType === typeFilter);
    if (topicFilter !== 'הכל') result = result.filter(b => b.topics?.includes(topicFilter));
    if (search.trim()) {
      const q = search.trim();
      result = result.filter(b => b.title?.includes(q));
    }
    // The list arrives newest first; other sorts work on a copy
    if (sortMode === 'oldest') {
      // Longest without an update first (lastUpdated is the last Knesset change, not submission); undated last
      result = result.slice().sort((a, b) => {
        if (!a.lastUpdated) return b.lastUpdated ? 1 : 0;
        if (!b.lastUpdated) return -1;
        return a.lastUpdated.localeCompare(b.lastUpdated);
      });
    } else if (sortMode === 'progressed') {
      result = result.slice().sort((a, b) =>
        stageInfo(a.simpleStage).order - stageInfo(b.simpleStage).order || (b.lastUpdated || '').localeCompare(a.lastUpdated || ''));
    }
    return result;
  }, [bills, stageFilter, typeFilter, topicFilter, search, sortMode]);

  // Stage counts: from meta/billStats, else counted from the full list
  const stageCounts = useMemo(() => {
    if (stats) return stats.byStage;
    const counts: Record<string, number> = {};
    if (complete) for (const b of bills || []) { const s = b.simpleStage || 'אחר'; counts[s] = (counts[s] || 0) + 1; }
    return counts;
  }, [stats, bills, complete]);
  const total = stats ? stats.total : complete && bills ? bills.length : 0;

  const visibleBills = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;
  const filtersActive = !isDefaultView;

  /* ── Filter chips (phone rows and the desktop grid share them) ── */

  const chipVariant = isDesktop ? 'flat' : 'raised';
  const stageChip = (stage: string) => (
    <Chip
      key={stage}
      variant={chipVariant}
      block={isDesktop}
      selected={stageFilter === stage}
      onClick={() => setStageFilter(stage)}
      count={stage === 'הכל' ? (total || undefined) : stageCounts[stage]}
    >
      {stage === 'הכל' ? 'כל השלבים' : (isDesktop && SHORT_STAGE[stage]) || stage}
    </Chip>
  );
  const plainChip = (key: string, label: string, active: boolean, onClick: () => void) => (
    <Chip key={key} variant={chipVariant} block={isDesktop} selected={active} onClick={onClick}>{label}</Chip>
  );
  const typeChip = (type: TypeFilter) => plainChip(type, type === 'הכל' ? 'כל הסוגים' : typeLabels[type], typeFilter === type, () => setTypeFilter(type));
  const topicChip = (topic: string) => plainChip(topic, topic === 'הכל' ? 'כל הנושאים' : topic, topicFilter === topic, () => setTopicFilter(topic));
  const sortChip = (mode: SortMode, label: string) => plainChip(mode, label, sortMode === mode, () => setSortMode(mode));

  // The funnel: each bar filters the list by its stage
  const maxFunnel = Math.max(1, ...FUNNEL.map(s => stageCounts[s] || 0));
  const funnel = total > 0 && (
    <Card>
      <CardHeader title="משפך החקיקה" />
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: space.xs, minHeight: 96 }}>
        {FUNNEL.map(stage => {
          const count = stageCounts[stage] || 0;
          const active = stageFilter === stage;
          return (
            <button
              key={stage}
              type="button"
              aria-pressed={active}
              aria-label={`${stage}: ${count.toLocaleString('he-IL')} הצעות`}
              onClick={() => setStageFilter(active ? 'הכל' : stage)}
              style={{ ...buttonReset, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: space.xs }}
            >
              <span style={{ fontSize: font.small, fontWeight: weight.bold, color: active ? color.primary : color.text }}>{count.toLocaleString('he-IL')}</span>
              <span style={{ width: '100%', height: Math.max((count / maxFunnel) * 56, 4), borderRadius: `${radius.sm}px ${radius.sm}px 0 0`, background: active ? color.primary : '#b9d2ec', transition: 'background 0.15s' }} />
              {/* Every label reserves two lines, so a wrapping label doesn't lift its bar */}
              <span style={{ fontSize: font.caption, lineHeight: '14px', minHeight: 28, textAlign: 'center', color: active ? color.primary : color.textSecondary, fontWeight: active ? weight.bold : weight.regular }}>
                {SHORT_STAGE[stage] || stage}
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: font.small, color: color.textSecondary, textAlign: 'center', marginTop: space.sm }}>
        {(stageCounts['נעצרה'] || 0).toLocaleString('he-IL')} נעצרו · {(stageCounts['מוזגה'] || 0).toLocaleString('he-IL')} מוזגו
      </div>
    </Card>
  );

  // Funnel, search and filter chips. Desktop: a sticky column beside the results.
  const filtersPanel = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: space.md, marginBottom: isDesktop ? 0 : space.md }}>
      {funnel}
      <SearchField value={search} onChange={setSearch} placeholder="חיפוש הצעת חוק" variant="raised" />
      {isDesktop ? (
        // Desktop: one card, a labelled section per kind of filter, chips on an even grid
        <Card>
          <FilterSection label="שלב בחקיקה">
            {stageChip('הכל')}
            <Grid cols={{ desktop: 2 }} gap={6}>{STAGE_ORDER.map(s => stageChip(s))}</Grid>
          </FilterSection>
          <FilterSection label="סוג הצעה">
            {typeChip('הכל')}
            <Grid cols={{ desktop: 3 }} gap={6}>{(['private', 'government', 'committee'] as const).map(t => typeChip(t))}</Grid>
          </FilterSection>
          <FilterSection label="נושא">
            {topicChip('הכל')}
            <Grid cols={{ desktop: 3 }} gap={6}>{ALL_TOPICS.map(t => topicChip(t))}</Grid>
          </FilterSection>
          <FilterSection label="מיון" last>
            {/* The two date directions side by side, progress below (three across would wrap) */}
            <Grid cols={{ desktop: 2 }} gap={6}>{SORT_OPTIONS.filter(o => o.key !== 'progressed').map(o => sortChip(o.key, o.label))}</Grid>
            {SORT_OPTIONS.filter(o => o.key === 'progressed').map(o => sortChip(o.key, o.label))}
          </FilterSection>
        </Card>
      ) : (
        // Phone: one row, one menu per kind of filter; the sort is on the result line
        <ChipRow>
          <SelectMenu variant="chip" label="שלב" title="שלב בחקיקה" value={stageFilter} neutralValue="הכל" onChange={setStageFilter}
            options={[{ key: 'הכל', label: 'כל השלבים', count: total || undefined }].concat(PHONE_STAGES.map(s => ({ key: s, label: s, count: stageCounts[s] })))} />
          <SelectMenu variant="chip" label="סוג" title="סוג הצעה" value={typeFilter} neutralValue="הכל" onChange={setTypeFilter}
            options={(['הכל', 'private', 'government', 'committee'] as TypeFilter[]).map(t => ({ key: t, label: t === 'הכל' ? 'כל הסוגים' : typeLabels[t], count: t === 'הכל' ? undefined : stats ? stats.byType[t] : undefined }))} />
          <SelectMenu variant="chip" label="נושא" value={topicFilter} neutralValue="הכל" onChange={setTopicFilter}
            options={[{ key: 'הכל', label: 'כל הנושאים' } as SelectOption<string>].concat(ALL_TOPICS.map(t => ({ key: t, label: t, count: stats ? stats.byTopic[t] : undefined })))} />
        </ChipRow>
      )}
    </div>
  );

  const clearFilters = () => setSearchParams(new URLSearchParams(), { replace: true });

  // The Knesset the bill list describes (from meta/billStats, so the label never runs
  // ahead of the list while a new Knesset's first sync is under way)
  const billsKnesset = viewingCurrent ? (stats && stats.knessetNumber) || scope.knesset : viewKnesset;
  // A new Knesset with no bills yet: say so and offer the previous Knesset's
  const previous = previousKnesset(scope);
  const earlyEmpty = viewingCurrent && previous !== null && listReady && total === 0 && !!bills && bills.length === 0;

  return (
    <Layout width="full">
      <PageContainer>
        <div style={isDesktop ? undefined : { padding: `${space.sm}px ${space.lg}px ${space.lg}px` }}>
          <PageHeader
            title="חקיקה"
            subtitle={total > 0
              ? `${total.toLocaleString('he-IL')} הצעות חוק בכנסת ה-${billsKnesset} · ${(stageCounts['עברה'] || 0).toLocaleString('he-IL')} הפכו לחוק`
              : `הצעות החוק בכנסת ה-${billsKnesset}`}
            action={<KnessetMenu value={viewKnesset} onChange={setKnesset} />}
            style={{ marginBottom: space.lg }}
          />

          {viewingCurrent && pausedSince && (
            <Notice tone="warning" icon="clock" style={{ marginBottom: space.lg }}>
              <b>הנתונים נכונים ל-{formatDayMonth(pausedSince)}.</b> הכנסת ה-{scope.knesset + 1} הושבעה, והעדכון יחודש כשהאתר יעבור אליה.
            </Notice>
          )}

          {earlyEmpty && previous !== null ? (
            <Card padding={0}>
              <EmptyState
                icon="document"
                title="עוד לא הוגשו הצעות חוק"
                body={`הכנסת ה-${scope.knesset} הושבעה${swornInLabel(scope) ? ` ב-${swornInLabel(scope)}` : ''}. הצעות חוק יופיעו כאן כשחברי הכנסת יתחילו להגיש אותן.`}
                action={<Button variant="primary" onClick={() => setKnesset(previous)}>להצעות החוק של הכנסת ה-{previous}</Button>}
              />
            </Card>
          ) : (
          <TwoPane side={filtersPanel} sideWidth={300}>
            {error && <Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card>}

            {!error && !listReady && <div data-skeleton="true"><SkeletonList rows={8} /></div>}

            {!error && listReady && (
              <>
                {isDesktop ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: font.small, color: color.textSecondary, marginBottom: space.sm }}>
                    <span>
                      {filtersActive
                        ? `${filtered.length.toLocaleString('he-IL')} תוצאות${total ? ` מתוך ${total.toLocaleString('he-IL')}` : ''}`
                        : `${(total || filtered.length).toLocaleString('he-IL')} הצעות חוק, מהעדכנית ביותר`}
                    </span>
                    {filtersActive && (
                      <button type="button" onClick={clearFilters} style={{ ...buttonReset, color: color.primary, fontWeight: weight.medium, minHeight: 32 }}>ניקוי הסינון</button>
                    )}
                  </div>
                ) : (
                  // Phone: the count (and a reset when filtered), then the sort menu
                  <ResultBar style={{ marginBottom: space.xs }} action={<SelectMenu label="מיון" value={sortMode} onChange={setSortMode} options={SORT_OPTIONS} />}>
                    {filtersActive
                      ? `${filtered.length.toLocaleString('he-IL')} מתוך ${(total || filtered.length).toLocaleString('he-IL')}`
                      : `${(total || filtered.length).toLocaleString('he-IL')} הצעות חוק`}
                    {filtersActive && (
                      <button type="button" onClick={clearFilters} style={{ ...buttonReset, color: color.primary, fontWeight: weight.medium, minHeight: TAP, marginInlineStart: space.sm }}>ניקוי</button>
                    )}
                  </ResultBar>
                )}

                {filtered.length === 0
                  ? (
                    <Card padding={0}>
                      <EmptyState title="לא נמצאו הצעות חוק" body={search.trim() ? `אין הצעת חוק שמתאימה ל"${search.trim()}".` : undefined} action={<Button variant="ghost" onClick={clearFilters}>הצגת כל הצעות החוק</Button>} />
                    </Card>
                  )
                  : (
                    <Card padding={0}>
                      {visibleBills.map((bill, i) => {
                        const ended = ENDED.includes(bill.simpleStage || '');
                        return (
                          <div key={bill.id} style={{ opacity: ended ? 0.7 : 1 }}>
                            <ListRow
                              divider={i > 0}
                              to={`/legislation/${bill.id}`}
                              state={{ from: 'חקיקה' }}
                              title={<span style={{ textDecoration: bill.simpleStage === 'נעצרה' ? 'line-through' : 'none' }}>{bill.title}</span>}
                              subtitle={
                                <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.xxs }}>
                                  <span style={{ display: 'flex', gap: space.xs, flexWrap: 'wrap' }}>
                                    <StageBadge stage={bill.simpleStage} />
                                    {bill.isBasicLaw && <Badge tone="special">חוק יסוד</Badge>}
                                    <Badge tone={bill.billType === 'government' ? 'info' : 'neutral'}>{typeLabels[bill.billType || ''] || bill.billType}</Badge>
                                  </span>
                                  <span>
                                    {[bill.topics && bill.topics.length > 0 ? bill.topics.slice(0, 2).join(' · ') : null,
                                      bill.lastUpdated ? `עודכן ${new Date(bill.lastUpdated).toLocaleDateString('he-IL')}` : null].filter(Boolean).join(' · ')}
                                  </span>
                                </span>
                              }
                            />
                          </div>
                        );
                      })}
                    </Card>
                  )}

                {hasMore && (
                  <Button fullWidth onClick={() => setVisibleCount(visibleCount + PAGE_SIZE)} style={{ marginTop: space.md }}>
                    הצגת עוד ({(filtered.length - visibleCount).toLocaleString('he-IL')} נוספות)
                  </Button>
                )}
              </>
            )}
          </TwoPane>
          )}
        </div>
      </PageContainer>
    </Layout>
  );
};

export default LegislationPage;
